import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, QrCode, Menu, X } from 'lucide-react';

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleDashboardClick = () => {
    navigate('/dashboard', { replace: true });
  };

  const pricing = {
    starter: {
      name: 'Starter',
      price: '$9',
      duration: '/month',
      features: [
        '1 active forms',
        'Basic analytics',
        '100 responses per month'
      ],
      cta: 'Start free trial',
      mostPopular: false,
    },
    professional: {
      name: 'Professional',
      price: '$29',
      duration: '/month',
      features: [
        '15 forms',
        'Unlimited responses',
        'Email notifications'
      ],
      cta: 'Start free trial',
      mostPopular: true,
    },
    enterprise: {
      name: 'Enterprise',
      price: '$199',
      duration: '/month',
      features: [
        '100 forms',
        'Unlimited responses',
        'Email notifications'
      ],
      cta: 'Start free trial',
      mostPopular: false,
    },
  };

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <header className="relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center border-b-2 border-gray-100 py-6 md:justify-start md:space-x-10">
            <div className="flex justify-start lg:w-0 lg:flex-1">
              <Link to="/" className="flex items-center">
                <QrCode size={32} className="text-blue-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">QR Feedback</span>
              </Link>
            </div>
            
            <div className="-mr-2 -my-2 md:hidden">
              <button
                type="button"
                className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className="sr-only">Open menu</span>
                <Menu size={24} aria-hidden="true" />
              </button>
            </div>
            
            <nav className="hidden md:flex space-x-10">
              <Link to="/pricing" className="text-base font-medium text-blue-600 hover:text-blue-700">
                Pricing
              </Link>
              <Link to="/about" className="text-base font-medium text-gray-500 hover:text-gray-900">
                About
              </Link>
            </nav>
            
            <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0">
              {user ? (
                <button
                  onClick={handleDashboardClick}
                  className="ml-8 whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="whitespace-nowrap text-base font-medium text-gray-500 hover:text-gray-900"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="ml-8 whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden z-10">
            <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y-2 divide-gray-50">
              <div className="pt-5 pb-6 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <QrCode size={32} className="text-blue-600" />
                    <span className="ml-2 text-xl font-bold text-gray-900">QR Feedback</span>
                  </div>
                  <div className="-mr-2">
                    <button
                      type="button"
                      className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="sr-only">Close menu</span>
                      <X size={24} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <nav className="grid gap-y-8">
                    <Link
                      to="/pricing"
                      className="-m-3 p-3 flex items-center rounded-md hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="ml-3 text-base font-medium text-blue-600">
                        Pricing
                      </span>
                    </Link>
                    <Link
                      to="/about"
                      className="-m-3 p-3 flex items-center rounded-md hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="ml-3 text-base font-medium text-gray-900">
                        About
                      </span>
                    </Link>
                  </nav>
                </div>
              </div>
              <div className="py-6 px-5 space-y-6">
                <div>
                  {user ? (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleDashboardClick();
                      }}
                      className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Dashboard
                    </button>
                  ) : (
                    <>
                      <Link
                        to="/signup"
                        className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Sign up
                      </Link>
                      <p className="mt-6 text-center text-base font-medium text-gray-500">
                        Existing customer?{' '}
                        <Link 
                          to="/login" 
                          className="text-blue-600 hover:text-blue-500"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Sign in
                        </Link>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:flex-col sm:align-center">
          <h1 className="text-5xl font-extrabold text-gray-900 sm:text-center">Pricing Plans</h1>
          <p className="mt-5 text-xl text-gray-500 sm:text-center">
            Start collecting customer feedback with our 14-day free trial. No credit card required.
          </p>
        </div>
        <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0">
          {Object.values(pricing).map((tier) => (
            <div
              key={tier.name}
              className={`rounded-lg shadow-sm divide-y divide-gray-200 ${
                tier.mostPopular ? 'border-2 border-blue-500' : 'border border-gray-200'
              }`}
            >
              {tier.mostPopular && (
                <div className="bg-blue-500 rounded-t-lg py-1.5">
                  <p className="text-xs font-medium text-white text-center">Most popular</p>
                </div>
              )}
              <div className="p-6 bg-white">
                <h2 className="text-lg leading-6 font-medium text-gray-900">{tier.name}</h2>
                <p className="mt-8">
                  <span className="text-4xl font-extrabold text-gray-900">{tier.price}</span>
                  <span className="text-base font-medium text-gray-500">{tier.duration}</span>
                </p>
                <Link
                  to={user ? '/dashboard/billing' : '/signup'}
                  className={`mt-8 block w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium text-center ${
                    tier.mostPopular
                      ? 'text-white bg-blue-600 hover:bg-blue-700'
                      : 'text-blue-600 bg-white border-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
              <div className="pt-6 pb-8 px-6 bg-white">
                <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">
                  What's included
                </h3>
                <ul className="mt-6 space-y-4">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex space-x-3">
                      <CheckCircle
                        className="flex-shrink-0 h-5 w-5 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-gray-500">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-24 lg:px-8">
        <div className="max-w-3xl mx-auto divide-y-2 divide-gray-200">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Frequently asked questions
          </h2>
          <dl className="mt-6 space-y-6 divide-y divide-gray-200">
            <div className="pt-6">
              <dt className="text-lg">
                <span className="font-medium text-gray-900">
                  What's included in the free trial?
                </span>
              </dt>
              <dd className="mt-2 text-base text-gray-500">
                During your 14-day free trial, you'll have access to all Professional plan features,
                including unlimited forms, advanced analytics, and custom branding options. No credit
                card is required to start your trial.
              </dd>
            </div>

            <div className="pt-6">
              <dt className="text-lg">
                <span className="font-medium text-gray-900">
                  Can I upgrade or downgrade my plan?
                </span>
              </dt>
              <dd className="mt-2 text-base text-gray-500">
                Yes, you can change your plan at any time. When upgrading, you'll have immediate access
                to the new features. When downgrading, the changes will take effect at the start of
                your next billing cycle.
              </dd>
            </div>

            <div className="pt-6">
              <dt className="text-lg">
                <span className="font-medium text-gray-900">
                  How does QR code sticker ordering work?
                </span>
              </dt>
              <dd className="mt-2 text-base text-gray-500">
                You can order professional-quality QR code stickers directly through our platform. We
                offer various sizes and materials suitable for different environments. Stickers are
                printed and shipped within 2-3 business days.
              </dd>
            </div>

            <div className="pt-6">
              <dt className="text-lg">
                <span className="font-medium text-gray-900">Do you offer refunds?</span>
              </dt>
              <dd className="mt-2 text-base text-gray-500">
                We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied with
                our service, contact our support team within 30 days of your purchase for a full
                refund.
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-700">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Ready to improve your customer feedback?</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-blue-200">
            Start your 14-day free trial today. No credit card required.
          </p>
          <Link
            to="/signup"
            className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 sm:w-auto"
          >
            Get started for free
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <Link to="/" className="flex items-center">
              <QrCode size={24} className="text-blue-600" />
              <span className="ml-2 text-lg font-bold text-gray-900">QR Feedback</span>
            </Link>
          </div>
          <nav className="mt-8 flex flex-wrap justify-center" aria-label="Footer">
            <div className="px-5 py-2">
              <Link to="/pricing" className="text-base text-gray-500 hover:text-gray-900">
                Pricing
              </Link>
            </div>
            <div className="px-5 py-2">
              <Link to="/about" className="text-base text-gray-500 hover:text-gray-900">
                About
              </Link>
            </div>
            <div className="px-5 py-2">
              <Link to="/privacy" className="text-base text-gray-500 hover:text-gray-900">
                Privacy
              </Link>
            </div>
            <div className="px-5 py-2">
              <Link to="/terms" className="text-base text-gray-500 hover:text-gray-900">
                Terms
              </Link>
            </div>
          </nav>
          <p className="mt-8 text-center text-base text-gray-400">
            &copy; 2025 QR Feedback, Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;